<template>
  <div class="card" style="min-height: calc(100vh - 180px)">
    <div class="bg-blue-light p-1">
        <TitleButton
            btnTitle="Add New"
            :title="'Sales Pipeline'"
            :showBtn="false"
            :showAddNew="true"
            @onClickAddNewButton="onClickAddNewButton"
        />
    </div>

    <div class="my-2 px-2">
        <DateQuerySetter
            btnTitle="Go"
            @onClickGo="onClickGo"
        >
            <div class="col-12 col-sm-6 col-md-4 col-lg-3 mr-top-1">
                <label class="form-label">Task Assign to</label>
                <AsyncSelect
                    placeholder="Task Assign to"
                    v-model="taskAssignId"
                    :api-service="fetchContactProfiles"
                    :reduce="text => text.id"
                />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3 mr-top-1">
                <label class="form-label">Campaign / Project</label>
                <AsyncSelect
                    placeholder="Project"
                    v-model="projectId"
                    :api-service="fetchProjects"
                    :reduce="text => text.id"
                />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3 mr-top-1">
                <label class="form-label">Source</label>
                <v-select
                    placeholder="Select Deal Source"
                    :options="dealSourceList" v-model="dealSourceId"
                    :reduce="option => option.id"
                    label="name"
                />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3 mr-top-1">
                <label class="form-label">Select Deal Owner</label>
                <AsyncSelect
                    placeholder="Deal Owner"
                    v-model="dealOwnerId"
                    :api-service="fetchContactProfiles"
                    :reduce="text => text.id"
                    :additional-query="{ type: 'contact_owner' }"
                />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3 mr-top-1">
                <label class="form-label">Select Deal Assign to</label>
                <AsyncSelect
                    placeholder="Deal Assign to"
                    v-model="dealAssignId"
                    :api-service="fetchContactProfiles"
                    :reduce="text => text.id"
                />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3 mr-top-1">
                <label class="form-label">Select Deal Stage</label>
                <v-select
                    placeholder="Select Deal Stage"
                    :options="dealStageList" 
                    v-model="dealStageId"
                    :reduce="option => option.id"
                    label="name"
                />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3 mr-top-1">
                <label class="form-label">Tag Register List</label>
                <AsyncSelect
                    placeholder="Tag Register List"
                    v-model="tagIds"
                    :api-service="fetchTagRegister"
                    :reduce="text => text"
                    :is-multiple="true"
                />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3 mr-top-1">
                <label class="form-label"> Product/Service </label>
                <AsyncSelect
                  placeholder="Select Product/Service"
                  :api-service="fetchProductList"
                  v-model="productIds"
                  label="text"
                  :reduce="text => text"
                  :format-label="option => option.text"
                  :multiple="true"
                />
            </div>

          <div class="col-12 col-sm-6 col-md-4 col-lg-3 mr-top-1">

            <div class="d-flex align-items-center justify-content-between filter-label">
              <label for="colFormLabel" class="col-form-label">Party: </label>
              <span class="filter-search-icon cursor-pointer" @click="onOpenContactSearchModal">
                            <i class="fas fa-search"></i>
                        </span>
            </div>

            <AsyncSelect
                placeholder="Select Party"
                v-model="contactProfile"
                :api-service="fetchContactProfiles"
                :additional-query="additionalContactOption"
                :reduce="text => text.id"
            />
          </div>

        </DateQuerySetter>
    </div>
    
    <div class="col-12 px-2 mt-2">
        <ListTable 
            :pipeline-list="pipelineList"
        />
        <div class="px-2 bottom-0 mt-2">
            <Pagination
                :offset="offset"
                @onPageChange="onPageChange"
                ref="pagination"
            />
        </div>
    </div>

    <ContactDropdownFilter
        v-if="store.state.isModalOpenThree"
        @onSearchContact="onSearchContact"
        :companyRoles="companyRoles"
        :isFilterable="true"
    />

    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, inject } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from "vuex";
import Loader from "@/components/atom/LoaderComponent.vue";
import TitleButton from '@/components/atom/TitleButton.vue';
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
import Pagination from '@/components/atom/Pagination';
import ListTable from '@/components/molecule/company/admin/sale-pipeline/List.vue';
import handleContact from "@/services/modules/contact";
import handleProjects from "@/services/modules/procurement/project";
import handlePurchase from '@/services/modules/purchase';
import handleTagRegister from '@/services/modules/scm/tag-register';
import handleHospital from '@/services/modules/hospital';
import handleAdmin from '@/services/modules/admin/admin';
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'
import handleRole from "@/services/modules/role";

const { fetchContactProfiles } = handleContact();
const { fetchProjects } = handleProjects();
const { fetchProductList } = handlePurchase();
const { fetchTagRegister } = handleTagRegister();
const { fetchNoteMasterList } = handleHospital();
const { fetchPipelineList } = handleAdmin();
const { fetchCompanyDefaultRoles } = handleRole();

const route = useRoute();
const router = useRouter();
const store = useStore();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false);

const start = computed(() => route.query.start);
const end = computed(() => route.query.end);
const page = computed(() => route.query.page);
const companyId = computed(() => route.params.companyId);

const dealStageList = ref([]);
const dealSourceList = ref([]);
const pipelineList = ref([]);
const offset = ref(10);

const taskAssignId = ref(null);
const projectId = ref(null);
const dealSourceId = ref(null);
const contactProfile = ref(null);
const dealOwnerId = ref(null);
const dealAssignId = ref(null);
const dealStageId = ref(null);
const tagIds = ref([]);
const productIds = ref([]);
const additionalContactOption = ref({
  with_mobile: 1,
  dealer_id: '',
  roles: 'customer,lead'
});
const companyRoles = ref(null);

const onClickGo = () => {
    getPipelineList();
}

const onOpenContactSearchModal = () => {
  store.state.isModalOpenThree = true;
}

const onPageChange = async (page) => {
    let routeQuery = { ...route.query }
    routeQuery.page = page
    await router.push({path: route.path, query: routeQuery})
    await getPipelineList()
}

const setPaginationData = (data) => {
    store.commit('setPaginationData', data)
}

const setPagination = (data) => {
    setPaginationData({
      records: data.total,
      perPage: data.per_page,
      currentPage: data.current_page,
    })
}

const onSearchContact = (contact) => {
  contactProfile.value = contact
}

const getDealList = async (noteType, targetList) => {
    const query = `?company_id=${companyId.value}&note_type=${noteType}`;
     try {
        const res = await fetchNoteMasterList(query);
        if (!res.status) {
            return showError(res.message);
        }
        targetList.value = res.data.data;
    } catch (err) {
        console.error(err);
    }
};

const getPipelineList = async () => {
    loading.value = true;
    const query = getQuery();
     try {
        const res = await fetchPipelineList(query);
        if (!res.status) {
            showError(res.message);
            return store.commit("resetPagination")
        }
        pipelineList.value = res.data.data;
        if(pipelineList.value.length > 0) {
            setPagination(res.data)
        }
    } catch (err) {
        console.error(err);
    } finally {
        loading.value = false;
    }
};

const getQuery = () => {
    let query = '?company_id=' + companyId.value
    query += '&offset=' + offset.value
    if (start) query += '&start_date=' + start.value
    if (end) query += '&end_date=' + end.value
    if(taskAssignId.value) query += '&task_assign_id=' + taskAssignId.value
    if(projectId.value) query += '&project_master_id=' + projectId.value
    if(dealSourceId.value) query += '&deal_source_id=' + dealSourceId.value
    if(dealOwnerId.value) query += '&deal_owner_id=' + dealOwnerId.value
    if(dealAssignId.value) query += '&deal_assign_id=' + dealAssignId.value
    if(dealStageId.value) query += '&deal_stage_id=' + dealStageId.value
    if(contactProfile.value) query += '&contact_profile_id=' + contactProfile.value
    if (page.value) query += '&page=' + page.value
    if (tagIds.value.length > 0) {
        tagIds.value.forEach(id => {
            query += '&tag_ids[]=' + id
        })
    }
    if (productIds.value.length > 0) {
        productIds.value.forEach(id => {
            query += '&product_ids[]=' + id
        })
    }

    return query
};

const onClickAddNewButton = () => {
    router.push({
      name: 'open-deal',
      params: route.params,
      query: route.query
    })
}

const getCompanyRoles = async () => {
  const res = await fetchCompanyDefaultRoles(`?company_id=${companyId.value}&roles=customer,lead`);
  if (res.status) {
    companyRoles.value = res.data
  }
}

onMounted( () => {
    getDealList('deal_source', dealSourceList);
    getDealList('deal_stage', dealStageList);
    getPipelineList();
    getCompanyRoles()
})
</script>

<style scoped>
.mr-top-1 {
    margin-top: 1% !important;
}
</style>